import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["chatBox", "input", "sendBtn", "header", "sendIcon"]

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this.askPath = this.data.get("chat-ask-path") || "/medimind/ask"
    this.signInPath = this.data.get("chat-sign-in-path") || "/users/sign_in"

    this.loggedIn = this.data.get("chat-logged-in") === "true"
    this.chatId = this.data.get("chat-chat-id") || null

    this.lastMessageReceived = null
    this.lastMessageTime = null

    this.removeTypingBubble()
    this.setupTurboStreamListeners()
  }

  // Suggestion click handler
  suggestionClick(event) {
    const textElement = event.currentTarget.querySelector(".text")
    if (textElement) {
      this.sendMessage(null, textElement.textContent)
    }
  }

  deleteChat(event) {
    event.preventDefault()
    if (!confirm("Are you sure you want to clear this chat?")) return
    if (this.chatBoxTarget) this.chatBoxTarget.innerHTML = ""
    this.chatId = null
    this.element.dataset.chatChatId = ""
    this.removeTypingBubble()
    if (this.headerTarget) this.headerTarget.style.display = ""
    if (this.inputTarget) this.inputTarget.value = ""
    this.disableSendButton(false)
  }


  async sendMessage(event = null, text = null, { allowGuest = false } = {}) {
    if (event) event.preventDefault()
    text = text || this.inputTarget.value.trim()
    if (!text) return

    if (!this.loggedIn && !allowGuest) allowGuest = true

    this.hideHeader()
    this.appendMessage("You", this.escapeHtml(text), "outgoing")
    this.inputTarget.value = ""
    this.disableSendButton(true)
    this.appendTypingBubble()

    const payload = { prompt: text }
    if (!this.loggedIn || allowGuest) payload.guest = true
    if (this.chatId) payload.chat_id = this.chatId

    try {
      const res = await fetch(this.askPath, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify(payload)
      })

      const data = await res.json()

      if (data.enqueued && data.chat_id) {
        this.chatId = data.chat_id
        this.element.dataset.chatChatId = data.chat_id
        if (data.stream_source_html) this.injectStreamSource(data.stream_source_html)
        this.lastMessageTime = Date.now()

        setTimeout(() => {
          if (this.shouldShowFallback()) {
            this.removeTypingBubble()
            this.appendMessage("MediMind", "⚠️ I'm having trouble responding. Please try again.", "incoming")
          }
        }, 45000)
      } else if (data.response) {
        setTimeout(() => {
          this.removeTypingBubble()
          this.appendMessage("MediMind", this.formatText(data.response), "incoming")
        }, 1000)
      } else if (data.error) {
        setTimeout(() => {
          this.removeTypingBubble()
          this.appendMessage("MediMind", this.escapeHtml("⚠️ " + data.error), "incoming")
        }, 1000)
      }
    } catch (err) {
      this.removeTypingBubble()
      this.appendMessage("MediMind", this.escapeHtml("⚠️ Something went wrong, please try again."), "incoming")
    } finally {
      this.disableSendButton(false)
    }
  }

  appendMessage(sender, content, type) {
    const msg = document.createElement("div")
    msg.className = `message ${type}`
    msg.innerHTML = `
      <div class="message-content">
        <div class="meta"><strong>${this.escapeHtml(sender)}</strong></div>
        <div class="text">${content}</div>
      </div>`
    this.chatBoxTarget.appendChild(msg)
    this.scrollToBottom()
    if (type === "incoming") this.lastMessageReceived = Date.now()
  }

  appendTypingBubble() {
    if (document.getElementById("typing-indicator")) return
    const msg = document.createElement("div")
    msg.id = "typing-indicator"
    msg.className = "message incoming loading"
    msg.innerHTML = `<div class="message-content"><div class="text"><em>MediMind is typing...</em></div></div>`
    this.chatBoxTarget.appendChild(msg)
    this.scrollToBottom()
  }

  removeTypingBubble() {
    const el = document.getElementById("typing-indicator")
    if (el) el.remove()
  }

  hideHeader() {
    if (this.headerTarget) this.headerTarget.style.display = "none"
  }

  disableSendButton(state) {
    if (!this.sendBtnTarget) return
    const sendIcon = this.sendBtnTarget.dataset.sendIcon
    const hourglassIcon = this.sendBtnTarget.dataset.hourglassIcon
    if (state) {
      if (this.sendIconTarget && hourglassIcon) this.sendIconTarget.src = hourglassIcon
      this.sendBtnTarget.disabled = true
    } else {
      if (this.sendIconTarget && sendIcon) this.sendIconTarget.src = sendIcon
      this.sendBtnTarget.disabled = false
    }
  }

  scrollToBottom() {
    this.chatBoxTarget.scrollTop = this.chatBoxTarget.scrollHeight
  }

  formatText(text) {
    return text
      .replace(/\n/g, '<br>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
  }

  escapeHtml(unsafe) {
    return String(unsafe)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;")
  }

  injectStreamSource(streamSourceHtml) {
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = streamSourceHtml.trim()
    const streamSource = tempDiv.querySelector('turbo-cable-stream-source')
    if (streamSource) document.body.appendChild(streamSource)
  }

  shouldShowFallback() {
    if (!document.getElementById("typing-indicator")) return false
    if (this.lastMessageReceived && this.lastMessageReceived > this.lastMessageTime) return false
    return true
  }

  setupTurboStreamListeners() {
    document.addEventListener('turbo:before-stream-render', () => {
      this.removeTypingBubble()
    })
  }
}
